import { Graphics, Text } from 'pixi.js';
import { PixiGlobal } from '../PixiGlobal';
import { Element } from './Element.class';
import type { TextContent, TextStyleData } from '@/types';
import { ELEMENT_TYPE } from '@/constants';
import { showTextEditor } from '@/utils/canvasUtil';
import { createTextStyle } from '@/utils/pixiTextStyleUtil';

export class TextElement extends Element {
  protected background: Graphics | null = null;

  protected text: Text | null = null;

  /** 文本是否已完成首次布局初始化，用于区分首次加载和后续样式更新 */
  private _textInitialized: boolean = false;

  constructor() {
    super();
  }

  protected initText() {
    if (this.text) return;

    this.text = new Text({
      text: '',
      resolution: PixiGlobal.getTextResolution(),
      autoGenerateMipmaps: true,
      roundPixels: true
    });
    this.setTarget(this.text);
  }

  protected initBackground() {
    if (this.background) return;

    this.background = new Graphics();
    this.targetContainer.addChildAt(this.background, 0);
  }

  protected override setDefaultAttrs(): void {
    super.setDefaultAttrs();

    this.setDefaultTextData();
  }

  protected setDefaultTextData() {
    if (!this._data || this._data.type !== ELEMENT_TYPE.TEXT) return;

    if (!this._data.content) {
      this._data.content = { text: '事例文本' };
    }

    const textData = this._data.content as TextContent;

    if (!textData.style) {
      const styleData: TextStyleData = {
        font: { fontFamily: 'Arial', fontSize: 70 },
        fill: { fill: '#000000' }
      };

      textData.style = styleData;
    }
  }

  public override async load(): Promise<boolean> {
    if (!this.textData) return false;

    this.initText();

    this.text!.text = this.textData.text;
    this.text!.anchor.set(0.5, 0.5);

    //设置文本样式（同步更新文本和尺寸）
    this.updateTextStyle();

    this.applyAttrs();

    // 标记首次布局完成，后续样式更新将采用等比缩放策略
    this._textInitialized = true;

    return true;
  }

  /**
   * 同步文本大小
   * @returns
   */
  protected syncTextSize() {
    if (!this.text) return;
    const bounds = this.text.getLocalBounds();
    const actualW = Math.max(1, bounds.width);
    const actualH = Math.max(1, bounds.height);

    const ds = this.attrs.layout?.displayScale ?? 1;
    Object.assign(this.attrs, {
      width: Math.max(1, actualW / ds),
      height: Math.max(1, actualH / ds),
      originWidth: actualW,
      originHeight: actualH
    });
  }

  public override refresh(): void {
    this.updateTextStyle();
    super.refresh();
  }

  /**
   * 编辑器修改样式后刷新：以元素中点为中心等比缩放，保持位置不变。
   *
   * 与 refresh() 的区别：refresh() 会让 applyParentLayout 根据新尺寸重新计算位置，
   * 导致修改字号时元素偏移。此方法在 super.refresh() 前后保存/还原中点坐标，
   * 并同步更新 layout 百分比，确保缩放以元素自身中心为基准。
   */
  public refreshKeepCenter(): void {
    const centerX = this.x;
    const centerY = this.y;

    this.refresh();

    if (this.x !== centerX || this.y !== centerY) {
      this.x = centerX;
      this.y = centerY;
      this.attrs.x = centerX;
      this.attrs.y = centerY;
      this.syncLayoutFromView();
    }
  }

  /**
   * 更新多行属性
   */
  public updateTextMultiline() {
    if (!this.text || !this.textData) return;

    if (this.textData.style?.multiline?.wordWrap === false) {
      Object.assign(this.text.style, {
        wordWrap: false,
        breakWords: false
      });
      return;
    }

    const layout = this.attrs.layout;
    const ds = layout?.displayScale ?? 1;

    // 首次加载：使用 layout.width 设计百分比（syncTextSize 尚未测量实际宽度）
    // 初始化后：使用 attrs.width × displayScale（syncTextSize 已写入实测宽度，
    //   attrs.width = actualW / oldDs，乘以当前 ds 后与文本实际渲染宽度成比例，）
    //   避免 layout.width 固定百分比无法反映真实文本尺寸导致不同 displayScale 下换行不一致
    const wrapWidth =
      this._textInitialized && this.attrs.width
        ? this.attrs.width * ds
        : layout?.parentWidth && layout?.width
          ? layout.parentWidth * layout.width
          : (this.attrs.width ?? 100) * ds;

    Object.assign(this.text.style, {
      wordWrapWidth: wrapWidth,
      wordWrap: true,
      breakWords: true
    });
  }

  /**
   * 拖拽过程中实时更新文本换行宽度。
   *
   * 与 `setSize` / `syncAttrsFromView` 不同，此方法仅修改 PixiJS 层的
   * wordWrapWidth 与内部 originWidth/originHeight，**不更新** data.attrs 中的
   * width / height / x / y 等字段。这些字段留到 `onTransformEnd` 时通过
   * `syncAttrsFromView()` 统一同步。
   *
   * 技巧：文本默认 anchor=(0.5,0.5) 居中于容器原点。通过绘制一个与
   * wordWrapWidth 等宽的不可见背景矩形，使容器的 localBounds 反映换行边界
   * 而非文本视觉宽度，从而 Transformer box 始终与用户拖拽宽度对齐、无需位置补偿。
   *
   * @param newWidth - 新的换行宽度（世界坐标下的视觉宽度）
   */
  public resizeWordWrap(newWidth: number): void {
    if (!this.text) return;

    const safeWidth = Math.max(1, Math.round(newWidth));

    // 1. 更新文本换行宽度，触发重排
    this.text.style.wordWrapWidth = safeWidth;
    this.text.style.wordWrap = true;
    this.text.style.breakWords = true;

    // 2. 读取重排后的新 bounds
    const newBounds = this.text.getLocalBounds();
    const newH = Math.max(1, newBounds.height);

    // 3. 更新内部 origin 引用值（用于 scale 换算），但不更新 width/height
    Object.assign(this.attrs, {
      originWidth: safeWidth,
      originHeight: newH
    });

    // 4. 重置 PixiJS scale（文本已按新宽度重排，无需拉伸）
    this.scale.set(1, 1);

    // 5. 绘制不可见背景矩形，将容器 bounds 扩展到 wordWrapWidth，
    //    确保 Transformer box 与用户拖拽宽度对齐

    this.updateBackground(safeWidth, newH);
  }

  /**
   * 拖拽结束后调用：根据文本实际渲染尺寸修正元素的宽高和背景。
   *
   * 解决问题：当文本为短单行但变形框被拖得很宽时，松开手后元素宽度
   * 应贴合文本实际视觉宽度，而非保持拖拽时的 wordWrapWidth。
   */
  public fitToTextContent(): void {
    if (!this.text) return;

    // 复用 syncTextSize 同步文本实际尺寸到 attrs
    this.syncTextSize();

    // 重置 PixiJS scale（syncTextSize 不改变 scale）
    this.scale.set(1, 1);

    // 重绘背景矩形以匹配最终尺寸
    this.updateBackground(this.attrs.originWidth ?? 0, this.attrs.originHeight ?? 0);
  }

  public setText(text: string): void {
    this.textData.text = text;

    this.updateTextStyle();

    this.applyAttrs();
  }

  /**
   * 更新文本样式（同步更新文本和尺寸）
   *
   * 行为区分：
   * - 首次加载（_textInitialized = false）：使用 updateTextMultiline() 以 attrs.width
   *   作为 wordWrapWidth，保持现有初始化逻辑
   * - 后续样式更新且文本自然宽度变化（字号/字间距等）：测量新旧样式的自然宽度比例，
   *   按比例等比缩放 wordWrapWidth，使文本保持行数不变、以中点等比缩放
   * - 后续样式更新但自然宽度未变（如仅改颜色）：保持现有逻辑
   *
   * @returns
   */
  public updateTextStyle(): void {
    if (!this.text || !this.textData || !this.textData.style) return;

    // PixiJS TextStyle 默认 whiteSpace: 'normal' 会折叠前导空格（U+0020），
    // 导致首行缩进"消失"。将前导空格替换为 U+00A0（non-breaking space），
    // 该字符不在 PixiJS COLLAPSIBLE_SPACES 列表中，不会被折叠。
    const NBSP = String.fromCharCode(0xa0);
    const rawText = this.textData.text ?? '';
    this.text.text = /^\s/.test(rawText)
      ? rawText.replace(/^(\s+)/, (match) => NBSP.repeat(match.length))
      : rawText;

    const alpha = this.textData.style.fill?.alpha ?? 1;

    // 在替换样式前，保存当前换行宽度
    const oldWordWrapWidth = this.text.style.wordWrapWidth ?? this.attrs.originWidth ?? 100;

    // 初始化后：测量旧样式的文本自然宽度（关闭换行），用于等比缩放计算
    let oldNaturalWidth = 0;
    if (this._textInitialized) {
      this.text.style.wordWrap = false;
      oldNaturalWidth = this.text.getLocalBounds().width;
    }

    if (!this.textData.style.font?.fontFamily?.includes('toneoz')) {
      if (this.textData.style.layout?.lineHeight) this.textData.style.layout.lineHeight = 0;
    }

    const style = createTextStyle(this.textData.style);
    // 直接替换 TextStyle，避免 Object.assign 对 TextStyle 实例属性复制不完整

    if (style.fontFamily && style.fontFamily.includes('toneoz')) {
      style.padding = Math.ceil((style.fontSize ?? 26) * 0.5);
      style.lineHeight = Math.ceil((style.fontSize ?? 26) * 1.5);
    }

    // 阅读端 displayScale：将文本渲染尺寸缩放到实际显示大小
    // 注意：不使用 Math.round，保持 fontSize 与 wordWrapWidth 的比例在任何
    // displayScale 下精确一致，避免非整数倍缩放时因取整导致换行位置偏移。
    const ds = this.attrs.layout?.displayScale ?? 1;
    if (ds !== 1) {
      if (style.fontSize) style.fontSize = style.fontSize * ds;
      if (style.padding) style.padding = style.padding * ds;
      if (style.lineHeight) style.lineHeight = style.lineHeight * ds;
      if (style.letterSpacing) style.letterSpacing = style.letterSpacing * ds;
      if (style.stroke && typeof style.stroke === 'object') {
        const s = style.stroke as { width?: number };
        if (s.width) s.width = s.width * ds;
      }
      if (style.dropShadow && typeof style.dropShadow === 'object') {
        const sh = style.dropShadow as { distance?: number; blur?: number };
        if (sh.distance) sh.distance = sh.distance * ds;
        if (sh.blur) sh.blur = sh.blur * ds;
      }
    }

    this.text.style = style;
    this.text!.alpha = alpha;

    // 是否进行了等比缩放（用于后续 background 更新判断）
    let didProportionalScale = false;
    const wordWrapDisabled = this.textData.style.multiline?.wordWrap === false;

    if (wordWrapDisabled) {
      this.updateTextMultiline();
    } else if (this._textInitialized && oldNaturalWidth > 0) {
      // 测量新样式的文本自然宽度（关闭换行）
      this.text.style.wordWrap = false;
      const newNaturalWidth = this.text.getLocalBounds().width;

      // 文本自然宽度发生变化时（字号/字间距等影响字符宽度的属性改变），
      // 按比例缩放换行宽度，保持文本行数不变，实现以中点等比缩放的效果
      if (newNaturalWidth > 0 && Math.abs(newNaturalWidth - oldNaturalWidth) > 0.5) {
        const scaleRatio = newNaturalWidth / oldNaturalWidth;
        const newWordWrapWidth = Math.max(1, Math.round(oldWordWrapWidth * scaleRatio));

        Object.assign(this.text.style, {
          wordWrapWidth: newWordWrapWidth,
          wordWrap: true,
          breakWords: true
        });

        // 同步 originWidth，使后续 resize / syncSize 基于新的换行宽度
        Object.assign(this.attrs, {
          originWidth: newWordWrapWidth
        });

        // 重置 PixiJS scale：内容尺寸已按比例改变，无需容器再做拉伸
        // 保持 scale=(1,1) 确保元素以中点 (0,0) 为基准等比缩放
        this.scale.set(1, 1);

        didProportionalScale = true;
      } else {
        // 自然宽度未变（如仅改颜色）：保持原有换行设置
        this.updateTextMultiline();
      }
    } else {
      // 首次加载：保持原有逻辑
      this.updateTextMultiline();
    }

    this.syncTextSize();

    // 等比缩放后重绘背景，使 Transformer box 与新的文本尺寸对齐
    if (didProportionalScale) {
      this.updateBackground();
    }
  }

  /**
   * 设置文本样式数据
   * @param styleData
   * @returns
   */
  public setStyleData(styleData: TextStyleData): void {
    if (!this.textData) return;

    if (!this.textData.style) {
      this.textData.style = {};
    }

    Object.assign(this.textData.style, styleData);

    this.updateTextStyle();
  }

  public updateBackground(width?: number, height?: number) {
    this.initBackground();

    const targetWidth = width ?? this.elementWidth;
    const targetHeight = height ?? this.elementHeight;

    this.background?.clear();
    this.background
      ?.rect(-targetWidth / 2, -targetHeight / 2, targetWidth, targetHeight)
      .fill({ color: 0x000000, alpha: 0 });
  }

  protected override redraw(): void {
    super.redraw();
    this.updateBackground();
  }

  public startEditing(onEnd?: () => void): void {
    this._isEditing = true;
    showTextEditor(this, onEnd);
  }

  public override updateEditing(onEnd?: () => void): void {
    if (!this._isEditing) return;
    showTextEditor(this, onEnd);
  }

  public get textData() {
    this.setDefaultTextData();
    return this._data.content as TextContent;
  }

  public get styleData() {
    this.setDefaultTextData();

    return this.textData.style!;
  }

  public get textObj() {
    return this.text;
  }

  /** 文本最小渲染宽度，用字号近似（≈单字符宽度）+ 10px空余，不然可能会不够，
   * 用于 boundBoxFunc 限制 resize 下限
   * */
  public get minTextWidth(): number {
    return (this.textData?.style?.font?.fontSize ?? 26) + 10;
  }

  public override destroy(): void {
    if (this.background && !this.background.destroyed) {
      this.background.destroy();
    }
    if (this.text && !this.text.destroyed) {
      this.text.destroy({ texture: false, textureSource: false, style: false });
    }

    this.background = null;
    this.text = null;
    super.destroy();
  }
}
